/** Calls of the JSON API v1 (app/ARCHITECTURE.md, "HTTP surface"). */

import {
  ApiError,
  NetworkError,
  REQUESTED_WITH,
  isRecord,
  requestJson,
} from "../lib/http";
import {
  parseDownloads,
  parseJob,
  parseMe,
  parseSubscription,
  parseUpload,
} from "./types";
import type { Download, Job, Me, SubscriptionState, Upload } from "./types";

const API = "/api/v1";
// a 128 MB upload over a slow line takes a while; the server has its own limits
const UPLOAD_TIMEOUT_MS = 60 * 60 * 1000;

export async function getMe(signal?: AbortSignal): Promise<Me> {
  return parseMe(await requestJson("GET", `${API}/me`, { signal }));
}

export async function login(input: {
  readonly email: string;
  readonly password: string;
  readonly remember: boolean;
}): Promise<void> {
  await requestJson("POST", `${API}/auth/login`, { body: input });
}

export async function logout(): Promise<void> {
  await requestJson("POST", `${API}/auth/logout`);
}

export async function requestPasswordReset(email: string): Promise<void> {
  await requestJson("POST", `${API}/auth/password-reset`, { body: { email } });
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<void> {
  await requestJson("POST", `${API}/auth/password-reset/confirm`, {
    body: { token, new_password: newPassword },
  });
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  await requestJson("POST", `${API}/account/password`, {
    body: { old_password: oldPassword, new_password: newPassword },
  });
}

export async function cancelSubscription(): Promise<SubscriptionState> {
  const body = await requestJson("POST", `${API}/account/cancel`, { body: {} });
  return parseSubscription(isRecord(body) ? body["subscription"] : undefined);
}

interface NewJob {
  readonly tool: string;
  readonly uploads: readonly string[];
  readonly options: Readonly<Record<string, string>>;
  readonly url?: string;
  readonly filename?: string;
}

export async function createJob(
  job: NewJob,
  signal?: AbortSignal,
): Promise<Job> {
  return parseJob(
    await requestJson("POST", `${API}/jobs`, { body: job, signal }),
  );
}

export async function getJob(id: string, signal?: AbortSignal): Promise<Job> {
  const url = `${API}/jobs/${encodeURIComponent(id)}`;
  return parseJob(await requestJson("GET", url, { signal }));
}

export async function listDownloads(signal?: AbortSignal): Promise<Download[]> {
  return parseDownloads(
    await requestJson("GET", `${API}/downloads`, { signal }),
  );
}

export async function deleteDownload(id: string): Promise<void> {
  await requestJson("DELETE", `${API}/downloads/${encodeURIComponent(id)}`);
}

export async function deleteUpload(id: string): Promise<void> {
  await requestJson("DELETE", `${API}/uploads/${encodeURIComponent(id)}`);
}

/** The JSON value of a text, or null when it is not JSON (a proxy's error page). */
function parseJsonText(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null; // judged by the HTTP status instead
  }
}

interface UploadOptions {
  /** Called with the uploaded fraction (0..1) while the file is sent. */
  readonly onProgress: (fraction: number) => void;
  readonly signal: AbortSignal;
}

/** Upload one file (XMLHttpRequest: fetch cannot report upload progress). */
export function uploadFile(
  file: File,
  options: UploadOptions,
): Promise<Upload> {
  return new Promise<Upload>((resolve, reject) => {
    if (options.signal.aborted) {
      reject(new DOMException("The upload was cancelled", "AbortError"));
      return;
    }
    const xhr = new XMLHttpRequest();
    const abort = (): void => {
      xhr.abort();
    };
    const settle = (): void => {
      options.signal.removeEventListener("abort", abort);
    };
    xhr.open("POST", `${API}/uploads`);
    xhr.setRequestHeader("X-Requested-With", REQUESTED_WITH);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        options.onProgress(event.loaded / event.total);
      }
    });
    xhr.addEventListener("load", () => {
      settle();
      const body = parseJsonText(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(parseUpload(body));
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      } else {
        reject(ApiError.fromBody(xhr.status, body));
      }
    });
    xhr.addEventListener("error", () => {
      settle();
      reject(
        new NetworkError(
          "Failed to upload the file. Please check your connection.",
        ),
      );
    });
    xhr.addEventListener("timeout", () => {
      settle();
      reject(new NetworkError("The upload took too long. Please try again."));
    });
    xhr.addEventListener("abort", () => {
      settle();
      reject(new DOMException("The upload was cancelled", "AbortError"));
    });
    options.signal.addEventListener("abort", abort, { once: true });
    const form = new FormData();
    form.append("file", file, file.name);
    xhr.send(form);
  });
}
